import React, { useState, useContext } from "react";
import { toast } from "sonner";
import { AuthContext } from "../contexts/AuthContext";
import { OwnerAuthContext } from "../contexts/OwnerAuthContext";

const LS_DISMISSED = "notificationBannerDismissed";

const NotificationPermissionBanner = () => {
  const { user } = useContext(AuthContext);
  const { owner } = useContext(OwnerAuthContext);
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem(LS_DISMISSED) === "true"
  );
  const [activating, setActivating] = useState(false);

  const isLoggedIn = Boolean(user || owner);
  const role = owner ? "owner" : "user";
  const permission = typeof Notification !== "undefined" ? Notification.permission : "denied";
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
  const shouldShow =
    isLoggedIn &&
    permission === "default" &&
    !dismissed &&
    "Notification" in window &&
    isStandalone; // Only after user has added to home screen (see AddToHomeScreenBanner)

  const handleActiver = () => {
    if (activating) return;
    if (!("Notification" in window) || Notification.permission !== "default") return;

    // iOS: Permission request MUST be the first sync action in response to the tap.
    // No setState or await before this.
    const permissionPromise = Notification.requestPermission();
    setActivating(true);

    permissionPromise
      .then(async (perm) => {

        if (perm !== "granted") {
          if (perm === "denied") {
            toast.error("Notifications refusées. Activez-les dans les paramètres du navigateur.");
          } else {
            toast.error("Permission non accordée. Avez-vous vu la popup « Autoriser » ?");
          }
          return;
        }
        const fcm = await import("./FCMTokenHandler");
        const token = await fcm.getFreshFcmToken();
        if (token) {
          const result = await fcm.sendDeviceTokenToBackend(token, role, true);
          if (result && result.skipped) {
            toast.success("Notifications déjà activées sur cet appareil.");
          } else {
            toast.success("Notifications activées !");
          }
          setDismissed(true);
          sessionStorage.setItem(LS_DISMISSED, "true");
        } else {
          const errMsg = fcm.lastTokenError || "Token non obtenu";

          const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
          const isPWA = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
          if (isIOS && !isPWA) {
            toast.error("Sur iPhone : ajoutez l'app à l'écran d'accueil, puis réessayez.");
          } else if (errMsg.includes("messaging/") || errMsg.includes("permission") || errMsg.includes("blocked")) {
            toast.error(`Erreur FCM : ${errMsg.slice(0, 80)}. Vérifiez Firebase Console (domaine autorisé).`);
          } else {
            toast.error(`Token non obtenu. Ouvrez la console (F12) pour les détails.`);
          }
        }
      })
      .catch((err) => {

        toast.error("Erreur lors de l'activation des notifications.");
      })
      .finally(() => {
        setActivating(false);
      });
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(LS_DISMISSED, "true");
  };

  if (!shouldShow) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label="Activer les notifications"
    >
      <div className="w-full max-w-lg rounded-2xl border-2 border-brand/20 bg-white p-4 shadow-elevated">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand/15 text-2xl">
            🔔
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-gray-900">Activer les notifications</h3>
            <p className="mt-0.5 text-sm text-gray-600">
              Recevez des alertes pour vos commandes prêtes et vos repas planifiés.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={handleActiver}
                disabled={activating}
                className="rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-brand-dark disabled:opacity-70"
              >
                {activating ? "Activation..." : "Activer"}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50"
              >
                Plus tard
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Fermer"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionBanner;
